import React, { useState, useRef, useEffect } from "react";
import { API_URL } from "../../../constants/appConfig";
import myToast from "../../../components/toast";
import {
  Box,
  Paper,
  Typography,
  Button,
  ImageList,
  ImageListItem,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  AddPhotoAlternate as AddPhotoIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

const Index = ({ lang }) => {
  const [images, setImages] = useState([]);
  const [deletedImages, setDeletedImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [updatingImages, setUpdatingImages] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    getAllImage();
  }, []);

  const handleFileUpload = (event) => {
    try {
      const file = event.target.files[0];
      const reader = new FileReader();

      reader.onload = () => {
        const imageData = reader.result;
        const image = new Image();
        image.src = imageData;

        image.onload = () => {
          const width = image.width;
          const height = image.height;
          const aspectRatio = width / height;
          const wantedAspectRatio = 1920 / 400;

          if (aspectRatio.toFixed(2) !== wantedAspectRatio.toFixed(2)) {
            myToast({
              status: "error",
              message: "Resim boyutları uygun değil",
            });
            return;
          } else {
            const imageItem = {
              imageBase64Data: imageData,
              imageSection: "slider",
              imageActive: true,
              imageId: images.length + 1,
            };
            setNewImages((prevImages) => [...prevImages, imageItem]);
            const imageSetData = {
              imageBase64Data: imageData,
              imageSection: "slider",
              imageActive: true,
              imageId: images.length + 1,
            };
            setImages((prevImages) => [...prevImages, imageSetData]);
          }
        };
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error(error);
      myToast({
        status: "error",
        message: "Resim yüklenirken bir hata oluştu",
      });
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const handleImageRemove = (index) => {
    try {
      const updatedImages = [...images];
      const removedImage = updatedImages.splice(index, 1)[0];
      setImages(updatedImages);
      setDeletedImages((prevDeletedImages) => [
        ...prevDeletedImages,
        removedImage,
      ]);
      setNewImages((prevNewImages) =>
        prevNewImages.filter((_, idx) => idx !== index)
      );
    } catch (error) {
      console.error(error);
      myToast({
        status: "error",
        message: "Resim kaldırılırken bir hata oluştu",
      });
    }
  };

  const sendDeletedImages = async () => {
    try {
      for (const image of deletedImages) {
        const response = await fetch(
          `${API_URL}/image/delete?imageId=${image.imageId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("token")}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error("Failed to delete image");
        }
      }
      myToast({
        status: "success",
        message: "Resim/Resimler başarıyla silindi",
      });
    } catch (error) {
      console.error(error);
      myToast({
        status: "error",
        message: "Resim/Resimler silinirken bir hata oluştu",
      });
    }
  };

  const sendNewImages = async () => {
    try {
      for (const image of newImages) {
        if (
          !deletedImages.find(
            (deletedImage) => deletedImage.imageId === image.imageId
          )
        ) {
          const response = await fetch(API_URL + "/image/saveImage", {
            method: "POST",
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              imageBase64Data: image.imageBase64Data,
              imageSection: "slider",
              imageActive: true,
            }),
          });
          if (!response.ok) {
            myToast({
              status: "error",
              message: "Resim kaydedilirken bir hata oluştu",
            });
          } else {
            myToast({
              status: "success",
              message: "Resim başarıyla kaydedildi",
            });
          }
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getAllImage = async () => {
    try {
      const response = await fetch(
        `${API_URL}/image/getImagesBySection?section=slider&active=true&size=50000`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch images");
      }
      const data = await response.json();
      setImages(data.content);
    } catch (error) {
      console.error(error);
      myToast({
        status: "error",
        message: "Resimler alınırken bir hata oluştu",
      });
    }
  };

  const handleUpdateSliderImages = async () => {
    try {
      setUpdatingImages(true);
      await sendDeletedImages();
      await sendNewImages();
      setUpdatingImages(false);
    } catch (error) {
      console.error(error);
      myToast({
        status: "error",
        message: "Görseller güncellenirken bir hata oluştu",
      });
    }
  };

  return (
    <Paper 
      elevation={3}
      sx={{ 
        p: 4,
        borderRadius: 2,
        bgcolor: 'background.paper',
        maxWidth: 1400,
        mx: 'auto'
      }}
    >
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" color="primary.main" gutterBottom>
          Slider Görselleri
        </Typography>
        <Alert 
          severity="info" 
          sx={{ 
            '& .MuiAlert-message': { fontSize: '0.95rem' }
          }}
        >
          Slider görselini güncelleyebilirsiniz. Resimler 1920*400 px boyutlarında olmalıdır.
        </Alert>
      </Box>

      <Box 
        sx={{ 
          mb: 4,
          display: 'flex',
          gap: 2,
          alignItems: 'center'
        }}
      >
        <input
          accept="image/jpeg, image/png, image/jpg, image/heic"
          type="file"
          ref={fileInputRef}
          onChange={handleFileUpload}
          hidden
        />
        <Button
          variant="contained"
          startIcon={<AddPhotoIcon />}
          onClick={() => fileInputRef.current.click()}
          sx={{
            textTransform: 'none',
            px: 3,
            py: 1,
            '&:hover': { 
              transform: 'translateY(-2px)',
              boxShadow: 3
            },
            transition: 'all 0.2s'
          }}
        >
          Yeni Görsel Ekle
        </Button>
        <Typography variant="body2" color="text.secondary">
          {images.length} görsel yüklendi
        </Typography>
      </Box>

      {images.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 2,
              pb: 1,
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}
          >
            Mevcut Görseller
          </Typography>
          <ImageList 
            sx={{ 
              width: '100%',
              gap: 24,
              mb: 2
            }} 
            cols={1}
          >
            {images.map((image, index) => (
              <ImageListItem 
                key={image.imageId}
                sx={{
                  position: 'relative',
                  bgcolor: 'grey.50',
                  p: 2,
                  borderRadius: 2,
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: 'grey.100',
                    transform: 'translateY(-4px)',
                    boxShadow: 3,
                    '& .deleteButton': {
                      opacity: 1,
                      transform: 'translateY(0)'
                    }
                  }
                }}
              >
                <Box
                  component="img"
                  src={image.imageBase64Data}
                  alt={`slider_${index}`}
                  sx={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: 300,
                    objectFit: 'contain',
                    borderRadius: 1,
                    boxShadow: 1
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    p: 2,
                    display: 'flex',
                    gap: 1
                  }}
                >
                  <IconButton
                    className="deleteButton"
                    onClick={() => handleImageRemove(index)}
                    sx={{
                      bgcolor: 'error.main',
                      color: 'white',
                      opacity: 0,
                      transform: 'translateY(-10px)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: 'error.dark',
                        transform: 'translateY(0) scale(1.1)'
                      }
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    mt: 1, 
                    display: 'block',
                    color: 'text.secondary'
                  }}
                >
                  Slider Görsel #{index + 1}
                </Typography>
              </ImageListItem>
            ))}
          </ImageList>
        </Box>
      )}

      <Button
        variant="contained"
        fullWidth
        disabled={updatingImages}
        onClick={handleUpdateSliderImages}
        sx={{
          py: 2,
          textTransform: 'none',
          position: 'relative',
          fontSize: '1rem',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 3
          },
          transition: 'all 0.2s'
        }}
      >
        {updatingImages ? (
          <CircularProgress 
            size={24} 
            sx={{ 
              color: 'white',
              position: 'absolute'
            }} 
          />
        ) : (
          'Görselleri Güncelle'
        )}
      </Button>
    </Paper>
  );
};

export default Index;
